import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axiosInstance from '@/lib/axios';
import { AxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { toast } from '@/lib/toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SmartIdentifierInput } from '@/components/ui/smart-identifier-input';
import { FieldVariantProvider } from '@/components/ui/field-variant';
import { FormPasswordField } from '@/components/forms/form-password-field';

const requestSchema = z.object({
  identifier: z.string().min(2, { message: 'Email or Phone is required.' }),
});

const resetSchema = z
  .object({
    otp: z.string().length(6, { message: 'OTP must be 6 digits.' }),
    newPassword: z.string().min(6, { message: 'Password must be at least 6 characters.' }),
    confirmPassword: z.string().min(6),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type Step = 'request' | 'reset' | 'success';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledIdentifier = searchParams.get('identifier') || '';
  const [step, setStep] = useState<Step>('request');
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');

  const requestForm = useForm<z.infer<typeof requestSchema>>({
    resolver: zodResolver(requestSchema),
    defaultValues: { identifier: prefilledIdentifier },
  });

  const resetForm = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  async function onRequestSubmit(values: z.infer<typeof requestSchema>) {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/auth/forgot-password/request', values);
      toast.success('OTP Sent', { description: response.data.message });
      setIdentifier(values.identifier);
      if (response.data.email) {
        setMaskedPhone(response.data.email);
      } else {
        setMaskedPhone(response.data.phone);
      }
      setStep('reset');
    } catch (error) {
      const err = error as AxiosError<{ error: string }>;
      toast.error('Error', { description: err.response?.data?.error || 'Failed to send OTP.' });
    } finally {
      setLoading(false);
    }
  }

  async function onResetSubmit(values: z.infer<typeof resetSchema>) {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/auth/forgot-password/reset', {
        identifier,
        otp: values.otp,
        newPassword: values.newPassword,
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      toast.success('Success', { description: 'Password reset successfully!' });
      setStep('success');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (error) {
      const err = error as AxiosError<{ error: string }>;
      toast.error('Error', {
        description: err.response?.data?.error || 'Failed to reset password.',
      });
    } finally {
      setLoading(false);
    }
  }

  const containerVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <FieldVariantProvider variant="glass">
      <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-background">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px] -z-10" />
        <div className="absolute top-0 left-0 w-full h-full -z-20 opacity-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

        <Link
          to="/login"
          className="absolute top-8 left-8 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          Back to login
        </Link>

        <AnimatePresence mode="wait">
          {step === 'request' && (
            <motion.div
              key="request"
              variants={containerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="w-full max-w-md"
            >
              <Card className="glass-card border-border/10 shadow-2xl">
                <CardHeader className="space-y-1 text-center">
                  <div className="flex justify-center mb-4">
                    <img src="/milora-logo.png" alt="Milora" className="w-12 h-12 object-contain" />
                  </div>
                  <CardTitle className="text-3xl font-bold tracking-tight">
                    Forgot Password
                  </CardTitle>
                  <CardDescription>
                    Enter your email or phone to receive a WhatsApp OTP
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...requestForm}>
                    <form
                      onSubmit={requestForm.handleSubmit(onRequestSubmit)}
                      className="space-y-6"
                    >
                      <FormField
                        control={requestForm.control}
                        name="identifier"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email or Phone</FormLabel>
                            <FormControl>
                              <SmartIdentifierInput
                                value={field.value}
                                onValueChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full h-12 text-lg font-semibold bg-gradient-premium shadow-lg"
                        disabled={loading}
                      >
                        {loading ? 'Sending OTP...' : 'Send OTP'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {step === 'reset' && (
            <motion.div
              key="reset"
              variants={containerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="w-full max-w-md"
            >
              <Card className="glass-card border-border/10 shadow-2xl">
                <CardHeader className="space-y-1 text-center">
                  <div className="flex justify-center mb-4">
                    <img src="/milora-logo.png" alt="Milora" className="w-12 h-12 object-contain" />
                  </div>
                  <CardTitle className="text-3xl font-bold tracking-tight">
                    Reset Password
                  </CardTitle>
                  <CardDescription>We've sent a 6-digit OTP to {maskedPhone}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...resetForm}>
                    <form onSubmit={resetForm.handleSubmit(onResetSubmit)} className="space-y-4">
                      <FormField
                        control={resetForm.control}
                        name="otp"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>OTP Code</FormLabel>
                            <FormControl>
                              <Input
                                icon={<MessageSquare className="h-4 w-4" />}
                                placeholder="123456"
                                className="tracking-[0.5em] font-mono"
                                maxLength={6}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormPasswordField
                        control={resetForm.control}
                        name="newPassword"
                        label="New Password"
                        autoComplete="new-password"
                      />
                      <FormPasswordField
                        control={resetForm.control}
                        name="confirmPassword"
                        label="Confirm Password"
                        autoComplete="new-password"
                      />
                      <Button
                        type="submit"
                        className="w-full h-12 text-lg font-semibold bg-gradient-premium shadow-lg mt-4"
                        disabled={loading}
                      >
                        {loading ? 'Resetting...' : 'Reset Password'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
                <CardFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep('request')}
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                  >
                    Didn't get the code? Try again
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FieldVariantProvider>
  );
}
